// Persistent role banner shown across the in-app layouts (admin, employee, platform).
// Platform admin wins over org admin when both apply (matches PostLoginRedirect precedence).
import { useQuery } from '@tanstack/react-query';
import { Shield } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';
import { resolvePlatformAdmin } from '@/lib/platform/usePlatformAdmin';
import { getActiveAdminOrg } from '@/lib/admin/useAdminGuard';

export function AdminSessionBanner() {
  const { user, isLoading: authLoading } = useAuth();

  const platformQ = useQuery({
    queryKey: ['admin-banner-platform', user?.id],
    enabled: !!user?.id,
    queryFn: () => resolvePlatformAdmin(user!.id),
    staleTime: Infinity,
    gcTime: Infinity,
  });

  const orgQ = useQuery({
    queryKey: ['admin-banner-org', user?.id],
    enabled: !!user?.id,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('pulse_org_members')
        .select('org_id')
        .eq('user_id', user!.id)
        .eq('role', 'admin');
      if (error) throw error;
      const orgs = (data ?? []).map((r) => r.org_id).filter(Boolean) as string[];
      if (orgs.length === 0) return null;
      const preferred = getActiveAdminOrg();
      const chosen = preferred && orgs.includes(preferred) ? preferred : orgs[0];
      const { data: org } = await supabase
        .from('pulse_orgs')
        .select('name')
        .eq('id', chosen)
        .maybeSingle();
      return { orgId: chosen, orgName: (org?.name as string | undefined) ?? 'this tenant' };
    },
    staleTime: 60_000,
  });

  if (authLoading || !user) return null;
  if (platformQ.isLoading || orgQ.isLoading) return null;

  const isPlatform = platformQ.data === true;
  const orgInfo = orgQ.data;

  if (isPlatform) {
    return (
      <div
        role="status"
        aria-live="polite"
        className="w-full bg-destructive text-destructive-foreground px-4 py-1.5 flex items-center justify-center gap-2 text-xs font-medium tracking-wide"
      >
        <Shield className="h-3.5 w-3.5" aria-hidden />
        <span>Signed in as Platform admin — actions can affect any tenant.</span>
      </div>
    );
  }

  if (orgInfo) {
    return (
      <div
        role="status"
        aria-live="polite"
        className="w-full bg-primary text-primary-foreground px-4 py-1.5 flex items-center justify-center gap-2 text-xs font-medium tracking-wide"
      >
        <Shield className="h-3.5 w-3.5" aria-hidden />
        <span>Signed in as Org admin — {orgInfo.orgName}.</span>
      </div>
    );
  }

  return null;
}
